import { TriggerbeeApi } from '../core/triggerbee/triggerbee-requester.service';
import { PasswordInputs } from './password-inputs';
import { UserRole } from './invite-user/invite-user';
import { UserResponse } from './user';
import { TimeZoneModel, UserModel } from '../account-settings/models/user.model';
import { HttpRequestDecorator } from '../core/triggerbee/http.request.decorator';
import { Injectable } from '@angular/core';
import { Logger, LoggerFactory } from '../core/logger.service';
import { Observable } from 'rxjs';
import { ApplicationStarterService } from 'app/core/application-starter.service';
import { PatchRequest } from 'app/shared/patch-request.model';
import {
  AddMultiFactorRequest,
  ConfirmTotpRegistrationResponse,
  MultiFactorInformation,
  StartTotpRegistrationResponse,
  VerifyTotpRequest,
} from './multi-factor-authentication';
@Injectable()
export class UserService {
  private readonly logger: Logger;

  constructor(
    loggerFactory: LoggerFactory,
    private http: HttpRequestDecorator,
  ) {
    this.logger = loggerFactory.get('UserService');
  }

  public resetPasswordSendEmail(email: string): Observable<void> {
    return this.http.post(
      `${this.http.getBaseUrl(TriggerbeeApi.Gateway)}/users/${email}/send-reset-password-email`,
      null,
    );
  }

  public signUpForEarlyAccess(): Observable<void> {
    const baseUrl = this.http.getBaseUrl(TriggerbeeApi.Gateway);
    return this.http.post(`${baseUrl}/users/sign-up-early-access`, null);
  }

  public async setPasswordFromActivationLink(passwordInputs: PasswordInputs): Promise<any> {
    const baseUrl = this.http.getBaseUrl(TriggerbeeApi.Gateway);
    const response = await this.http.put(`${baseUrl}/users/${passwordInputs.userEmail}/set-password`, {
      activateGuid: passwordInputs.id,
      password: passwordInputs.password,
    });
    return response.toPromise();
  }

  public resetPassword(passwordInputs: PasswordInputs): Observable<void> {
    const baseUrl = this.http.getBaseUrl(TriggerbeeApi.Gateway);
    return this.http.put(`${baseUrl}/users/${passwordInputs.userEmail}/reset-password`, {
      resetPasswordId: passwordInputs.id,
      password: passwordInputs.password,
    });
  }

  public async inviteUser(
    email: string,
    fullname: string,
    language: string,
    role: UserRole,
    accountName: string,
    extraSiteIds: number[] = [],
  ): Promise<boolean> {
    const baseUrl = this.http.getBaseUrl(TriggerbeeApi.Gateway);
    // A gateway from before S15 puts accountName in its email; a newer one ignores it and reads the names itself.
    await this.http
      .post(`${baseUrl}/users/invite-user`, {
        email: email,
        fullname: fullname,
        language: language,
        role: role,
        accountName: accountName,
        extraSiteIds: extraSiteIds,
      })
      .toPromise();
    ApplicationStarterService.triggerEvent('onTriggerbeeClientUserInvited', {
      email: email,
      fullName: fullname,
      language: language,
      role: role,
      accountName: accountName,
    });

    return true;
  }

  public resendInvitation(email: string): Observable<void> {
    const baseUrl = this.http.getBaseUrl(TriggerbeeApi.Gateway);
    return this.http.post(`${baseUrl}/users/${email}/reinvite-user`, null);
  }

  public resendAccountInvitation(email: string): Observable<void> {
    const baseUrl = this.http.getBaseUrl(TriggerbeeApi.Gateway);
    return this.http.post(`${baseUrl}/users/invitations/resend`, { email });
  }

  public revokeInvitation(email: string, allAccounts: boolean): Observable<void> {
    const baseUrl = this.http.getBaseUrl(TriggerbeeApi.Gateway);
    return this.http.post(`${baseUrl}/users/invitations/revoke`, { email, allAccounts });
  }

  public async getSupportedTimeZonesAsync(): Promise<TimeZoneModel[]> {
    const baseUrl = this.http.getBaseUrl(TriggerbeeApi.Gateway);
    return this.http.get(`${baseUrl}/timezones/all-supported`).toPromise();
  }

  public async getUser(): Promise<any> {
    const baseUrl = this.http.getBaseUrl(TriggerbeeApi.Gateway);
    return this.http.get(`${baseUrl}/users/current`).toPromise();
  }

  public getUserAsObservable(): Observable<UserModel> {
    const baseUrl = this.http.getBaseUrl(TriggerbeeApi.Gateway);
    return this.http.get(`${baseUrl}/users/current`);
  }

  public getUsersForAccount(siteId: number): Observable<UserResponse[]> {
    const baseUrl = this.http.getBaseUrl(TriggerbeeApi.Gateway);
    return this.http.get(`${baseUrl}/users/accounts/${siteId}`);
  }

  public patchCurrentUser(operations: PatchRequest[]): Observable<UserModel> {
    const baseUrl = this.http.getBaseUrl(TriggerbeeApi.Gateway);
    return this.http.patch(`${baseUrl}/users/current`, operations);
  }

  public getUiPreferences(): Observable<string | null> {
    const baseUrl = this.http.getBaseUrl(TriggerbeeApi.Gateway);
    return this.http.getDownload(`${baseUrl}/users/current/ui-preferences`);
  }

  public updateUiPreferences(value: string): Observable<void> {
    const baseUrl = this.http.getBaseUrl(TriggerbeeApi.Gateway);
    return this.http.put(`${baseUrl}/users/current/ui-preferences`, value);
  }

  /** Fire-and-forget variant of updateUiPreferences that survives page unload. */
  public updateUiPreferencesOnUnload(value: string): void {
    const baseUrl = this.http.getBaseUrl(TriggerbeeApi.Gateway);
    fetch(`${baseUrl}/users/current/ui-preferences`, {
      method: 'PUT',
      keepalive: true,
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: value,
    });
  }

  public patchUser(userId: string, operations: PatchRequest[]): Observable<UserModel> {
    const baseUrl = this.http.getBaseUrl(TriggerbeeApi.Gateway);
    return this.http.patch(`${baseUrl}/users/${userId}`, operations);
  }

  public deleteUser(email: string): Observable<UserModel> {
    const baseUrl = this.http.getBaseUrl(TriggerbeeApi.Gateway);
    return this.http.delete(`${baseUrl}/users/${email}`);
  }

  public addMultiFactorAuthenticationTotp(request: AddMultiFactorRequest): Observable<StartTotpRegistrationResponse> {
    const baseUrl = this.http.getBaseUrl(TriggerbeeApi.Gateway);
    return this.http.post(`${baseUrl}/users/multifactor/add/totp`, request);
  }

  public removeMultiFactorAuthentication(email: string, code: string): Observable<void> {
    const baseUrl = this.http.getBaseUrl(TriggerbeeApi.Gateway);
    return this.http.post(`${baseUrl}/users/multifactor/remove`, {
      code: code,
      email: email,
    });
  }

  public removeMultiFactorAuthenticationAdmin(email: string): Observable<void> {
    const baseUrl = this.http.getBaseUrl(TriggerbeeApi.Gateway);
    return this.http.delete(`${baseUrl}/admin/users/multifactor/remove/${email}`);
  }

  public completeTotpRegistration(request: VerifyTotpRequest): Observable<ConfirmTotpRegistrationResponse> {
    const baseUrl = this.http.getBaseUrl(TriggerbeeApi.Gateway);
    return this.http.post(`${baseUrl}/users/multifactor/complete/totp`, request);
  }

  public canGetMfa(email: string): Observable<boolean> {
    const baseUrl = this.http.getBaseUrl(TriggerbeeApi.Gateway);
    return this.http.get(`${baseUrl}/users/multifactor/can-get/${email}`);
  }

  public multiFactorInformation(email: string): Observable<MultiFactorInformation> {
    const baseUrl = this.http.getBaseUrl(TriggerbeeApi.Gateway);
    return this.http.get(`${baseUrl}/users/multifactor/information/${email}`);
  }
}
